import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Building2, ChevronRight, Download, Home, RotateCcw, TrendingDown, TrendingUp, X } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { getCurrentUser } from '../hooks/useAuth';
import { useFacilityTypes } from '../hooks/useFacilities';
import { analyticsApi } from '../lib/api-client';
import type { DecisionsAnalyticsPageDto } from '../types/api';

const DECISION_COLORS: Record<string, string> = {
  COMPLIANT: '#10b981',
  NON_COMPLIANT: '#ef4444',
  NO_ACTION: '#6b7280',
  WARNING: '#f59e0b',
  FINES: '#f97316',
  CLOSURE: '#dc2626',
  TEMPORARY_CLOSING: '#b91c1c',
  PERMANENT_CLOSING_OF_THE_FACILITY: '#7f1d1d',
  RELOCATION_OF_THE_HOUSEHOLD: '#8b5cf6',
};

const DEFAULT_COLOR = '#94a3b8';

function getDecisionColor(decision: string): string {
  return DECISION_COLORS[decision] ?? DEFAULT_COLOR;
}

function formatDecisionLabel(decision: string): string {
  return decision
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

function formatRwf(amount: number): string {
  return `RWF ${amount.toLocaleString()}`;
}

type AssessmentTypeTab = 'ALL' | 'HYGIENE' | 'COMPLIANCE';

interface BreadcrumbItem {
  id: string;
  name: string;
  type: string;
}

interface Filters {
  startDate: string;
  endDate: string;
  facilityTypeId: string;
}

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconColor,
  iconBg,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
}) {
  return (
    <Card>
      <CardContent className='p-5'>
        <div className='flex items-start justify-between'>
          <div>
            <p className='text-sm font-medium text-muted-foreground mb-1'>{title}</p>
            <p className='text-2xl font-bold text-foreground'>{value}</p>
            {subtitle && <p className='text-xs text-muted-foreground mt-1'>{subtitle}</p>}
          </div>
          <div className={`p-2.5 rounded-lg ${iconBg}`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DecisionsAnalytics() {
  const user = getCurrentUser();

  const [activeTab, setActiveTab] = useState<AssessmentTypeTab>('ALL');
  const [filters, setFilters] = useState<Filters>({ startDate: '', endDate: '', facilityTypeId: '' });
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const { data: facilityTypes } = useFacilityTypes();

  const currentParentLocationId = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].id : undefined;

  const queryParams = {
    ...(activeTab !== 'ALL' ? { assessmentType: activeTab } : {}),
    ...(filters.startDate ? { startDate: filters.startDate } : {}),
    ...(filters.endDate ? { endDate: filters.endDate } : {}),
    ...(filters.facilityTypeId ? { facilityTypeId: filters.facilityTypeId } : {}),
    ...(currentParentLocationId ? { parentLocationId: currentParentLocationId } : {}),
  };

  const { data, isLoading, isError, error } = useQuery<DecisionsAnalyticsPageDto>({
    queryKey: ['decisions-analytics', activeTab, filters, currentParentLocationId],
    queryFn: () => analyticsApi.getDecisionsAnalytics(queryParams),
  });

  const hasActiveFilters =
    activeTab !== 'ALL' ||
    !!filters.startDate ||
    !!filters.endDate ||
    !!filters.facilityTypeId ||
    breadcrumb.length > 0;

  function handleReset() {
    setActiveTab('ALL');
    setFilters({ startDate: '', endDate: '', facilityTypeId: '' });
    setBreadcrumb([]);
  }

  function handleLocationDrillDown(locationId: string, locationName: string, locationType: string) {
    setBreadcrumb(prev => [...prev, { id: locationId, name: locationName, type: locationType }]);
  }

  function handleBreadcrumbClick(index: number) {
    if (index === -1) {
      setBreadcrumb([]);
    } else {
      setBreadcrumb(prev => prev.slice(0, index + 1));
    }
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      const blob = await analyticsApi.exportDecisionsAnalytics(queryParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'decisions-analytics.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }

  // Compute summary metrics
  const compliantCount = data?.decisions.find(d => d.decision === 'COMPLIANT')?.count ?? 0;
  const nonCompliantCount =
    data?.decisions
      .filter(d => d.decision !== 'COMPLIANT' && d.decision !== 'NO_ACTION')
      .reduce((sum, d) => sum + d.count, 0) ?? 0;

  const pieData =
    data?.decisions.map(d => ({
      name: formatDecisionLabel(d.decision),
      value: d.count,
      color: getDecisionColor(d.decision),
      original: d.decision,
    })) ?? [];

  return (
    <div className='min-h-screen bg-background'>
      {/* Page header */}
      <div className='border-b border-border bg-white px-4 sm:px-6 lg:px-8 py-4'>
        <div className='max-w-[1600px] mx-auto'>
          <div className='flex items-start justify-between gap-4 flex-wrap'>
            <div>
              <h1 className='text-xl font-bold text-foreground'>Decisions Analytics</h1>
              <p className='text-sm text-muted-foreground mt-0.5'>
                Overview of assessment decisions and enforcement actions by location
              </p>
            </div>
            <Button onClick={handleExport} loading={isExporting} variant='outline' size='sm' className='gap-2'>
              <Download className='h-4 w-4' />
              Export Excel
            </Button>
          </div>

          {/* Assessment type tabs */}
          <div className='flex gap-1 mt-4 bg-muted rounded-lg p-1 w-fit'>
            {(['ALL', 'HYGIENE', 'COMPLIANCE'] as AssessmentTypeTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab
                    ? 'bg-white text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab === 'ALL' ? 'All Types' : tab.charAt(0) + tab.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className='max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6'>
        {/* Filters bar */}
        <div className='flex flex-wrap gap-3 items-end'>
          <div className='w-48'>
            <Input
              type='select'
              label='Facility Type'
              placeholder='All facility types'
              value={filters.facilityTypeId}
              options={[
                { value: '', label: 'All facility types' },
                ...(facilityTypes ?? []).map(ft => ({ value: ft.id, label: ft.name })),
              ]}
              onChange={e => setFilters(f => ({ ...f, facilityTypeId: String(e.target.value) }))}
            />
          </div>
          <div className='w-44'>
            <Input
              type='date'
              label='Start Date'
              value={filters.startDate}
              onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
            />
          </div>
          <div className='w-44'>
            <Input
              type='date'
              label='End Date'
              value={filters.endDate}
              onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
            />
          </div>
          {hasActiveFilters && (
            <Button variant='ghost' size='sm' onClick={handleReset} className='gap-1.5 mb-0.5'>
              <RotateCcw className='h-3.5 w-3.5' />
              Reset
            </Button>
          )}
        </div>

        {/* Location breadcrumb */}
        {breadcrumb.length > 0 && (
          <div className='flex items-center gap-1.5 text-sm flex-wrap'>
            <button
              onClick={() => handleBreadcrumbClick(-1)}
              className='flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors'
            >
              <Home className='h-3.5 w-3.5' />
              <span>All Locations</span>
            </button>
            {breadcrumb.map((item, i) => (
              <span key={item.id} className='flex items-center gap-1.5'>
                <ChevronRight className='h-3.5 w-3.5 text-muted-foreground' />
                {i < breadcrumb.length - 1 ? (
                  <button
                    onClick={() => handleBreadcrumbClick(i)}
                    className='text-muted-foreground hover:text-foreground transition-colors'
                  >
                    {item.name}
                  </button>
                ) : (
                  <span className='font-medium text-foreground'>{item.name}</span>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className='flex items-center justify-center py-24'>
            <div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary' />
          </div>
        )}

        {/* Error state */}
        {isError && !isLoading && (
          <div className='flex flex-col items-center justify-center py-16 gap-3 text-destructive'>
            <AlertCircle className='h-8 w-8' />
            <p className='text-sm font-medium'>Failed to load decisions analytics.</p>
            <p className='text-xs text-muted-foreground'>{String((error as any)?.message ?? '')}</p>
          </div>
        )}

        {!isLoading && !isError && data && (
          <>
            {/* Summary metric cards */}
            <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
              <MetricCard
                title='Total Facilities Assessed'
                value={data.totalFacilities.toLocaleString()}
                icon={Building2}
                iconColor='text-blue-600'
                iconBg='bg-blue-50'
              />
              <MetricCard
                title='Compliant'
                value={compliantCount.toLocaleString()}
                subtitle={
                  data.totalFacilities > 0
                    ? `${((compliantCount / data.totalFacilities) * 100).toFixed(1)}% of total`
                    : undefined
                }
                icon={TrendingUp}
                iconColor='text-emerald-600'
                iconBg='bg-emerald-50'
              />
              <MetricCard
                title='Enforcement Actions'
                value={nonCompliantCount.toLocaleString()}
                subtitle={
                  data.totalFacilities > 0
                    ? `${((nonCompliantCount / data.totalFacilities) * 100).toFixed(1)}% of total`
                    : undefined
                }
                icon={TrendingDown}
                iconColor='text-red-600'
                iconBg='bg-red-50'
              />
              <MetricCard
                title='Total Fines Collected'
                value={formatRwf(data.totalFines)}
                icon={Building2}
                iconColor='text-orange-600'
                iconBg='bg-orange-50'
              />
            </div>

            {/* Empty state */}
            {data.decisions.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-20 gap-3'>
                <Building2 className='h-10 w-10 text-muted-foreground' />
                <p className='text-sm font-medium text-foreground'>No data available</p>
                <p className='text-xs text-muted-foreground'>Try adjusting your filters or selecting a different date range.</p>
              </div>
            ) : (
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Left: Donut chart */}
                <Card>
                  <CardHeader className='pb-2'>
                    <CardTitle className='text-base'>Decision Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className='flex justify-center'>
                      <ResponsiveContainer width={320} height={280}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx='50%'
                            cy='50%'
                            innerRadius={70}
                            outerRadius={110}
                            paddingAngle={2}
                            dataKey='value'
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Legend */}
                    <div className='flex flex-wrap gap-2 mt-3 justify-center'>
                      {pieData.map(d => (
                        <span key={d.original} className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                          <span
                            className='inline-block h-2.5 w-2.5 rounded-full flex-shrink-0'
                            style={{ backgroundColor: d.color }}
                          />
                          {d.name}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Right: Ranked decision list */}
                <Card>
                  <CardHeader className='pb-2'>
                    <CardTitle className='text-base'>Decisions Ranked</CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-3'>
                    {data.decisions
                      .slice()
                      .sort((a, b) => b.count - a.count)
                      .map(d => (
                        <div key={d.decision}>
                          <div className='flex items-center justify-between mb-1'>
                            <div className='flex items-center gap-2'>
                              <span
                                className='inline-block h-2.5 w-2.5 rounded-full flex-shrink-0'
                                style={{ backgroundColor: getDecisionColor(d.decision) }}
                              />
                              <span className='text-sm font-medium text-foreground'>
                                {formatDecisionLabel(d.decision)}
                              </span>
                            </div>
                            <div className='flex items-center gap-3 text-sm'>
                              <span className='font-semibold text-foreground'>{d.count.toLocaleString()}</span>
                              <span className='text-muted-foreground w-12 text-right'>
                                {d.percentage.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                          {/* Bar indicator */}
                          <div className='h-1.5 w-full bg-muted rounded-full overflow-hidden'>
                            <div
                              className='h-full rounded-full transition-all duration-500'
                              style={{
                                width: `${Math.min(d.percentage, 100)}%`,
                                backgroundColor: getDecisionColor(d.decision),
                              }}
                            />
                          </div>
                          {d.decision === 'FINES' && d.totalChargedAmount != null && (
                            <p className='text-xs text-orange-600 mt-0.5'>
                              Total fined: {formatRwf(d.totalChargedAmount)}
                            </p>
                          )}
                        </div>
                      ))}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* By Location section */}
            {data.byLocation && data.byLocation.length > 0 && (
              <Card>
                <CardHeader className='pb-2'>
                  <CardTitle className='text-base'>
                    Breakdown by Location
                    {data.childLocationType && (
                      <span className='ml-2 text-xs font-normal text-muted-foreground'>
                        ({data.childLocationType})
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                      <thead>
                        <tr className='border-b border-border'>
                          <th className='text-left py-2 px-3 font-medium text-muted-foreground'>Location</th>
                          <th className='text-right py-2 px-3 font-medium text-muted-foreground'>Total</th>
                          {/* Show distinct decision columns from the first row */}
                          {(data.byLocation[0]?.byDecision ?? []).map(bd => (
                            <th
                              key={bd.decision}
                              className='text-right py-2 px-3 font-medium text-muted-foreground whitespace-nowrap'
                            >
                              {formatDecisionLabel(bd.decision)}
                            </th>
                          ))}
                          <th className='py-2 px-3' />
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-border'>
                        {data.byLocation.map(loc => (
                          <tr
                            key={loc.locationId}
                            className='hover:bg-muted/40 cursor-pointer transition-colors'
                            onClick={() =>
                              handleLocationDrillDown(loc.locationId, loc.locationName, loc.locationType)
                            }
                          >
                            <td className='py-2.5 px-3'>
                              <span className='font-medium text-foreground'>{loc.locationName}</span>
                              <span className='ml-1.5 text-xs text-muted-foreground'>({loc.locationType})</span>
                            </td>
                            <td className='py-2.5 px-3 text-right font-semibold'>{loc.total.toLocaleString()}</td>
                            {loc.byDecision.map(bd => (
                              <td key={bd.decision} className='py-2.5 px-3 text-right'>
                                <span
                                  className='font-medium'
                                  style={{ color: getDecisionColor(bd.decision) }}
                                >
                                  {bd.count.toLocaleString()}
                                </span>
                              </td>
                            ))}
                            <td className='py-2.5 px-3 text-muted-foreground'>
                              <ChevronRight className='h-4 w-4' />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
